import { useMutation } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { identityEntityCreatePersonEntityMutation } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@column/ui';

import { useEntityApiMode } from '../_util/entityApiMode';
import {
  PersonEntityFormValues,
  buildPersonCreateRequest,
  createDefaultPersonFormValues,
} from '../_util/useEntityForm';
import { NotificationList } from '~/components';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useNotificationStore } from '~/stores/Notification';

import { PersonEntityFields } from './PersonEntityFields';

export interface CreatedPerson {
  id: string;
  name: string;
}

interface AddPersonEntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Called once the person entity has been created; the caller links it to the business.
  onCreated: (person: CreatedPerson) => void;
}

const personDisplayName = (values: PersonEntityFormValues): string =>
  [values.firstName, values.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

/**
 * A create-a-person modal that leads with the fields the API requires to create a PERSON entity —
 * name, date of birth, address, and identification (SSN for U.S. citizens, or a foreign document
 * otherwise) — and surfaces the remaining optional sections (Employment & Income, Account Activity,
 * Compliance) collapsed beneath the address, mirroring the full entity form. On success the caller
 * links the new person to the business it is building via {@link onCreated}.
 */
export const AddPersonEntityModal: React.FC<AddPersonEntityModalProps> = ({ isOpen, onClose, onCreated }) => {
  const { addSuccessNotification, addDangerNotification, clearNotifications } = useNotificationStore();
  // Missing or invalid fields are listed inside the dialog, like the API errors below.
  const { handleSubmitErrors } = useFormErrorHandler({ defaultDisplayType: 'popup' });

  const form = useForm<PersonEntityFormValues>({
    mode: 'onChange',
    defaultValues: createDefaultPersonFormValues(),
    shouldUnregister: false,
  });

  const createPersonMutation = useMutation(identityEntityCreatePersonEntityMutation());
  // On the root entity page the person must be created in that tab's live/sandbox mode (not the
  // global session mode), so it can be linked to the root business afterward. Undefined elsewhere.
  const apiMode = useEntityApiMode();

  // Start each open from a clean slate so a previous draft (or a stale in-modal error) never
  // leaks into a new person.
  useEffect(() => {
    if (isOpen) {
      form.reset(createDefaultPersonFormValues());
      clearNotifications('popup');
    }
  }, [isOpen, form, clearNotifications]);

  const handleCreate = form.handleSubmit(async (values) => {
    try {
      const response = await createPersonMutation.mutateAsync({
        body: buildPersonCreateRequest(values),
        ...(apiMode?.requestOptions ?? {}),
      });
      const name =
        [response.person_details?.first_name, response.person_details?.last_name].filter(Boolean).join(' ') ||
        personDisplayName(values);
      addSuccessNotification({ content: `${name} added` });
      onCreated({ id: response.id, name });
      onClose();
    } catch (error: any) {
      addDangerNotification({ content: error?.message ?? 'Unable to create person', display: 'popup' });
    }
  }, handleSubmitErrors);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent overlayScroll className="gap-3 sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Link New Person</DialogTitle>
          <DialogDescription>
            Create a person entity with the required details. It will be linked to this business automatically.
          </DialogDescription>
        </DialogHeader>

        {/* Rendered inside the dialog so validation/create errors stack above the modal backdrop
            (the global notification list sits below the dialog's z-50 overlay). `!m-0` keeps it from
            adding dead space above the form when there are no errors. */}
        <NotificationList display="popup" spacing="bottom" className="!m-0" />

        {/* Full-bleed so each Section's own padding and top divider align with the page frame:
            negate the dialog's p-6 and hand the sections a matching --page-margin. Pull up to trim
            the first section's top padding so the form doesn't sit too far below the header. */}
        <FormProvider {...form}>
          <div className="-mx-6 -mt-2 [--page-margin:24px]">
            {/* Shares the Person Entity page's field definitions so the two can't drift; the modal
                just drops the contact fields and the leading section headline. */}
            <PersonEntityFields />
          </div>
        </FormProvider>

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={createPersonMutation.isPending}
          >
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={handleCreate} disabled={createPersonMutation.isPending}>
            <Icon.Plus />
            {createPersonMutation.isPending ? 'Creating…' : 'Create Entity & Link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
